import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, StatusBadge } from '@/components/UI';
import {
    describePromotionExposure,
    formatWallClock,
    getPromotionStatusTone,
    PROMOTION_STATUS_LABELS,
    type IdeaLabPromotionReceipt
} from '../ideaLabUtils';

interface PromotionReceiptCardProps {
    promotion: IdeaLabPromotionReceipt;
    /** Label of the promoted variant when the caller has it; the receipt only carries ids. */
    variantLabel?: string | undefined;
}

/**
 * One promotion receipt: what was created, whether the compiler moved underneath the run, and the
 * post-commit entry-parity verification. Re-verify is available while verification is pending or
 * failed; a passed receipt is final.
 */
const PromotionReceiptCard = ({ promotion, variantLabel }: PromotionReceiptCardProps) => {
    const queryClient = useQueryClient();
    const needsUniversePin = promotion.universe.mode === 'runEntities' && promotion.universe.entityListId === null;
    const verifyMutation = useMutation({
        ...trpc.ideaLab.verifyPromotion.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (receipt) => {
            showToast(PROMOTION_STATUS_LABELS[receipt.status], receipt.status === 'verification_passed' ? 'success' : 'info');
            await Promise.all([
                queryClient.invalidateQueries(trpc.ideaLab.getPromotion.pathFilter()),
                queryClient.invalidateQueries(trpc.ideaLab.listPromotions.pathFilter())
            ]);
        }
    });

    return (
        <div className="space-y-3 rounded border border-zinc-800 bg-black/20 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="font-medium text-zinc-100">
                        <Link className="text-blue-300 hover:text-blue-200" to={`/user/tradingStrategy/${promotion.strategyId}`}>
                            {promotion.strategyName}
                        </Link>
                        <span className="ml-2 text-xs text-zinc-500">strategy #{promotion.strategyId}, inactive</span>
                    </div>
                    <div className="text-xs text-zinc-500">
                        {variantLabel ? `${variantLabel} · ` : ''}run #{promotion.runId} · artifact #{promotion.artifactId} · promoted{' '}
                        {formatWallClock(promotion.createdAt)}
                    </div>
                </div>
                <StatusBadge status={promotion.status} tone={getPromotionStatusTone(promotion.status)}>
                    {PROMOTION_STATUS_LABELS[promotion.status]}
                </StatusBadge>
            </div>

            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-zinc-500">Trade Logic</dt>
                <dd className="text-zinc-300">
                    <Link className="text-blue-300 hover:text-blue-200" to={`/user/tradeLogic/entry/${promotion.definitionId}`}>
                        definition #{promotion.definitionId}
                    </Link>
                    , version #{promotion.versionId} · exit version #{promotion.exitVersionId}
                </dd>
                <dt className="text-zinc-500">Leg</dt>
                <dd className="text-zinc-300">
                    {promotion.lens === 'option' ? (
                        <>
                            option leg · {promotion.legOpenTradeType === 'buyToOpen' ? 'buy to open' : 'sell to open'} · pinned to{' '}
                            <Link className="text-blue-300 hover:text-blue-200" to="/user/optionBuyingStrategies">
                                option strategy #{promotion.optionBuyingStrategyId}
                            </Link>{' '}
                            ·{' '}
                        </>
                    ) : null}
                    {promotion.legDirection} {promotion.legEntityType} · {promotion.entryTransactionSizeValue}%{' '}
                    {promotion.entryTransactionSizeType === 'percentageValue' ? 'of value' : 'of risk capital'} ·{' '}
                    {promotion.lens === 'futures' ? 'ETF signal subjects' : 'underlying'}{' '}
                    {promotion.allowedUnderlyingEntityTypes.join(', ')}
                </dd>
                <dt className="text-zinc-500">Universe</dt>
                <dd className="text-zinc-300">
                    {promotion.universe.mode === 'allMonitored' ? (
                        <span className="text-amber-300">
                            all monitored assets of the allowed types (deliberately broader than the run)
                        </span>
                    ) : promotion.universe.entityListId !== null ? (
                        <>
                            the run&apos;s {promotion.universe.entityCount} entities via{' '}
                            <Link className="text-blue-300 hover:text-blue-200" to="/user/entityLists">
                                entity list #{promotion.universe.entityListId}
                            </Link>
                        </>
                    ) : (
                        <span className="text-amber-300">not pinned yet; re-run verification to attach the run&apos;s entity list</span>
                    )}
                </dd>
                <dt className="text-zinc-500">Evidence</dt>
                <dd className={promotion.exposure === null ? 'text-zinc-500' : 'text-zinc-300'}>
                    {describePromotionExposure(promotion.exposure)}
                </dd>
                <dt className="text-zinc-500">Family</dt>
                <dd className="text-zinc-300">
                    {promotion.familyId !== null ? (
                        <Link className="text-blue-300 hover:text-blue-200" to={`/user/strategyFamilies/${promotion.familyId}`}>
                            family #{promotion.familyId}
                        </Link>
                    ) : (
                        'none (no promoted parent or control yet)'
                    )}
                </dd>
                <dt className="text-zinc-500">Compiler</dt>
                <dd className="text-zinc-300">
                    {promotion.compiler.changed ? (
                        <span className="text-amber-300">
                            build changed since the run ({promotion.compiler.artifactBuildId.slice(0, 12)} →{' '}
                            {promotion.compiler.currentBuildId.slice(0, 12)}); both fingerprints matched
                        </span>
                    ) : (
                        `same build as the run (${promotion.compiler.currentBuildId.slice(0, 12)})`
                    )}
                </dd>
                <dt className="text-zinc-500">Verification</dt>
                <dd className="text-zinc-300">
                    {promotion.verification.sampleEntityCount} sample{' '}
                    {promotion.verification.sampleEntityCount === 1 ? 'entity' : 'entities'}, {promotion.verification.comparedEntryCount}{' '}
                    entries compared
                    {promotion.lens === 'option' ? `, ${promotion.verification.comparedSelectionCount} contract selections compared` : ''}
                    {promotion.verification.verifiedAt ? ` · ${formatWallClock(promotion.verification.verifiedAt)}` : ''}
                </dd>
            </dl>

            {promotion.verification.firstDifference && (
                <Callout title="Strategy path did not reproduce the measured entries" variant="error">
                    {promotion.verification.firstDifference}
                </Callout>
            )}
            {promotion.verification.error && (
                <Callout title="Verification could not complete" variant="warning">
                    {promotion.verification.error}
                </Callout>
            )}

            {(promotion.status !== 'verification_passed' || needsUniversePin) && (
                <div className="flex justify-end">
                    <Button
                        isLoading={verifyMutation.isPending}
                        size="sm"
                        variant="outline-dark"
                        onClick={() => verifyMutation.mutate({ promotionId: promotion.id })}
                    >
                        {promotion.status === 'verification_passed' ? "Pin the run's entities" : 'Re-run verification'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default PromotionReceiptCard;
